import { Grid, Button, TextField, FormControl, InputLabel, Select, MenuItem, Box, Typography, Switch, FormControlLabel } from "@mui/material";
import { useSelector } from "react-redux";
import { Formik, Form } from "formik";
import TextFieldValidated from "./TextFieldValidated";
import SectionSeasonDates from "./SectionSeasonDates";
import SectionCategories from "./SectionCategories";
import SectionContacts from "./SectionContacts";
import SectionAmenities from "./SectionAmenities";
import SectionNotes from "./SectionNotes";
import { validationSchema } from "./validationSchema";
import HotelPdfUpload from "./HotelPdfUpload";

const HotelForm = ({
  initialValues,
  onSubmit,
  onCancel,
  mode = "add",
  onBatchSave,
}) => {
  const configData = useSelector((state) => state.config.configData || {});
  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue, setValues }) => {
        const handleExtracted = (extracted) => {
          setValues((prevValues) => ({
            ...prevValues,
            destination: extracted.destination || prevValues.destination || "",
            sub_destination: extracted.sub_destination || prevValues.sub_destination || "",
            hotel_name: extracted.hotel_name || prevValues.hotel_name || "",
            type: (extracted.type && extracted.type !== "Hotel") ? extracted.type : (prevValues.type && prevValues.type !== "Hotel" ? prevValues.type : "Deluxe"),
            rating: typeof extracted.rating === "number" ? extracted.rating : (prevValues.rating ?? 3),
            address: extracted.address || prevValues.address || "",
            hotel_link: extracted.hotel_link || prevValues.hotel_link || "",
            isActive: extracted.isActive !== undefined ? extracted.isActive : (prevValues.isActive ?? true),
            contacts: (extracted.contacts && extracted.contacts.length > 0) ? extracted.contacts : (prevValues.contacts || [""]),
            amenities: (extracted.amenities && extracted.amenities.length > 0) ? extracted.amenities : (prevValues.amenities || [""]),
            notes: (extracted.notes && extracted.notes.length > 0) ? extracted.notes : (prevValues.notes || [""]),
            season_dates: (extracted.season_dates && extracted.season_dates.length > 0) ? extracted.season_dates : (prevValues.season_dates || []),
            category: (extracted.category && extracted.category.length > 0) ? extracted.category : (prevValues.category || [])
          }));
        };

        return (
        <Form>

          {/* AI PDF UPLOAD AUTOFILL */}
          <HotelPdfUpload onExtracted={handleExtracted} mode={mode} onBatchSave={onBatchSave} />

          {/* BASIC INFO */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextFieldValidated
                name="hotel_name"
                label="Hotel Name"
                {...{ values, errors, touched, handleChange, handleBlur }}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={values.type}
                  name="type"
                  label="Type"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.type && Boolean(errors.type)}
                >
                  {configData?.additionalCosts?.hotel?.map((hotelType) => (
                    <MenuItem key={hotelType.type} value={hotelType.type}>
                      {hotelType.type}
                    </MenuItem>
                  )) || ["Hotel", "Homestay", "Resort", "Guest House", "Villa", "Pension"].map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* DESTINATIONS */}
          <Grid container spacing={2} mt={1}>
            <Grid item xs={6}>
              <TextFieldValidated
                name="destination"
                label="Destination"
                {...{ values, errors, touched, handleChange, handleBlur }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextFieldValidated
                name="sub_destination"
                label="Sub Destination"
                {...{ values, errors, touched, handleChange, handleBlur }}
              />
            </Grid>
          </Grid>

          {/* ACTIVE STATUS TOGGLE */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1.5,
              mt: 2,
              borderRadius: 1.5,
              bgcolor: values.isActive !== false ? "#f0fdf4" : "#f8fafc",
              border: `1px solid ${values.isActive !== false ? "#bbf7d0" : "#e2e8f0"}`,
            }}
          >
            <Box>
              <Typography variant="subtitle2" fontWeight={600} color={values.isActive !== false ? "success.dark" : "text.secondary"}>
                Hotel Status: {values.isActive !== false ? "Active" : "Inactive"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {values.isActive !== false
                  ? "This hotel will be visible and selectable in the Create Inquiry form."
                  : "Inactive hotels will NOT be visible in the Create Inquiry form."}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={values.isActive !== false}
                  onChange={(e) => setFieldValue("isActive", e.target.checked)}
                  color="success"
                />
              }
              label={values.isActive !== false ? "Active" : "Inactive"}
              sx={{ m: 0 }}
            />
          </Box>

          {/* SEASON DATES */}
          <SectionSeasonDates
            {...{ values, errors, touched, handleChange, handleBlur, setFieldValue }}
          />

          {/* CATEGORIES */}
          <SectionCategories
            {...{ values, errors, touched, handleChange, handleBlur }}
          />

          {/* RATING */}
          <TextFieldValidated
            name="rating"
            label="Rating"
            type="number"
            {...{ values, errors, touched, handleChange, handleBlur }}
            sx={{ mt: 2 }}
          />

          {/* ADDRESS */}
          <TextFieldValidated
            name="address"
            label="Address"
            {...{ values, errors, touched, handleChange, handleBlur }}
            sx={{ mt: 2 }}
          />

          {/* HOTEL LINK — used by "Check your hotel" in the quotation PDF */}
          <TextFieldValidated
            name="hotel_link"
            label="Hotel Link (Google profile / Maps URL)"
            {...{ values, errors, touched, handleChange, handleBlur }}
            sx={{ mt: 2 }}
          />

          {/* CONTACTS */}
          <SectionContacts
            {...{ values, errors, touched, handleChange, handleBlur }}
          />

          {/* AMENITIES */}
          <SectionAmenities
            {...{ values, errors, touched, handleChange, handleBlur }}
          />

          {/* NOTES */}
          <SectionNotes
            {...{ values, errors, touched, handleChange, handleBlur }}
          />

          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              bgcolor: 'background.paper',
              zIndex: 10,
              py: 2,
              mt: 3,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Button
              type="submit"
              variant="contained"
              fullWidth
            >
              Save Hotel
            </Button>

            <Button
              variant="outlined"
              fullWidth
              sx={{ mt: 1 }}
              onClick={onCancel}
            >
              Cancel
            </Button>
          </Box>
          
          {Object.keys(errors).length > 0 && (
             <Box mt={2} p={2} bgcolor="#ffebee" color="error.main" borderRadius={1}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                   Please fix the following errors:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {(() => {
                    const errorMessages = [];
                    const extractErrors = (errObj) => {
                      if (!errObj) return;
                      if (typeof errObj === 'string') {
                        errorMessages.push(errObj);
                      } else if (Array.isArray(errObj)) {
                        errObj.forEach(item => extractErrors(item));
                      } else if (typeof errObj === 'object') {
                        Object.values(errObj).forEach(val => extractErrors(val));
                      }
                    };
                    extractErrors(errors);
                    return errorMessages.map((msg, idx) => (
                      <Typography component="li" variant="body2" key={idx}>
                        {msg}
                      </Typography>
                    ));
                  })()}
                </Box>
             </Box>
           )}

        </Form>
      );
    }}
  </Formik>
);
};

export default HotelForm;
